import { faCheck, faClock, faWarning } from '@fortawesome/pro-regular-svg-icons'
import { Anchor, Loader } from '@mantine/core'

import { getStoreCustomDomainStatus, getStoreDetails } from '@flecto/api/saas'
import { Trans, useTranslation } from '@flecto/translations'
import { Icon } from '@flecto/ui'
import { isValidDomain } from '@flecto/utils'

import { type useFormContext } from '../components/FormStoreSettings/FormStoreSettings'
import { useCustomDomainDescription } from './useCustomDomainDescription'

type UseCheckDomainOptions = {
  form: ReturnType<typeof useFormContext>
}

export function useCustomDomainCheck(options: UseCheckDomainOptions) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_store' })
  const { setDescription } = useCustomDomainDescription()
  const form = options?.form

  return async (customDomain?: string | null): Promise<boolean> => {
    const domain = String(customDomain ?? '').toLowerCase()

    if (!domain) {
      setDescription(null)
      return true
    }

    if (!isValidDomain(domain ?? '')) {
      setDescription(null)
      return false
    }

    try {
      setDescription({
        icon: <Loader size={10} />,
        text: t('section_custom_domain.domain_checking'),
        color: 'black',
      })

      const [store, domainStatus] = await Promise.all([
        getStoreDetails(),
        getStoreCustomDomainStatus(domain ?? ''),
      ])

      const isDomainAdded = Boolean(domainStatus?.added)
      const isDomainConfigured = Boolean(domainStatus?.configured)
      const isDomainSame = domain === store?.apex_domain
      const isDomainDiff = domain !== store?.apex_domain

      if (isDomainAdded && isDomainDiff) {
        form.setFieldError(
          'apex_domain',
          t('section_custom_domain.domain_already_added')
        )
        setDescription(null)
        return false
      }

      if (!isDomainAdded && !isDomainConfigured && isDomainDiff) {
        setDescription({
          icon: <Icon icon={faClock} color='dimmed' size='xs' />,
          text: t('section_custom_domain.domain_valid_but_not_configured'),
          color: 'dimmed',
        })
        return true
      }

      if (!isDomainAdded && isDomainConfigured && isDomainDiff) {
        setDescription({
          icon: <Icon icon={faCheck} color='green' size='xs' />,
          text: t('section_custom_domain.domain_valid_and_configured'),
          color: 'green',
        })
        return true
      }

      if (isDomainConfigured && isDomainSame) {
        setDescription({
          icon: <Icon icon={faCheck} color='green' size='xs' />,
          text: (
            <Trans
              ns='saas'
              i18nKey='profile_store.section_custom_domain.domain_activated'
              components={[
                <Anchor
                  key={0}
                  href={store?.apex_domain ?? ''}
                  target='_blank'
                />,
              ]}
            />
          ),
          color: 'green',
        })
        return true
      }

      if (!isDomainConfigured && isDomainSame) {
        setDescription({
          icon: <Icon icon={faWarning} color='orange' size='xs' />,
          text: t('section_custom_domain.domain_waiting_configuration'),
          color: 'orange',
        })
        return true
      }
    } catch {
      setDescription({
        icon: <Icon icon={faWarning} color='red' size='xs' />,
        text: t('section_custom_domain.error_while_loading_domain_config'),
        color: 'red',
      })
      return false
    }

    return true
  }
}
